import React, {
  useState,
  createContext,
  ReactElement,
  useCallback,
} from 'react';
import { FindAndReplace } from '../types';

interface FindAndReplaceContextType {
  findMetricId: string;
  replaceMetricId: string;
  isArchiveUnusedMetricSelected: boolean;
  setIsArchiveUnusedMetricSelected: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  updateById(
    id: string,
    action: 'add' | 'remove',
    type: FindAndReplace.AffectedContentType,
  ): void;
  setFindMetricId: React.Dispatch<React.SetStateAction<string>>;
  setReplaceMetricId: React.Dispatch<React.SetStateAction<string>>;
  initialAffectedContent: FindAndReplace.MetricReceipt | undefined;
  setInitialAffectedContent: React.Dispatch<
    React.SetStateAction<FindAndReplace.MetricReceipt | undefined>
  >;
  selectedAffectedContent: FindAndReplace.MetricReceipt | undefined;
  setSelectedAffectedContent: React.Dispatch<
    React.SetStateAction<FindAndReplace.MetricReceipt | undefined>
  >;
  isFindAndReplaceFlowActive: boolean;
}

const getKeyType = (type: FindAndReplace.AffectedContentType) => {
  if (
    type === 'performanceConfigurations' ||
    type === 'reports' ||
    type === 'compoundMetrics'
  ) {
    return;
  }
  return type;
};
const FindAndReplaceContext = createContext<FindAndReplaceContextType>({
  isFindAndReplaceFlowActive: false,
  findMetricId: '',
  isArchiveUnusedMetricSelected: false,
  replaceMetricId: '',
  setIsArchiveUnusedMetricSelected: () => null,
  updateById: () => null,
  setFindMetricId: () => '',
  setReplaceMetricId: () => '',
  initialAffectedContent: undefined,
  setInitialAffectedContent: () => null,
  selectedAffectedContent: undefined,
  setSelectedAffectedContent: () => null,
});

const FindAndReplaceProvider = ({ children }: { children: ReactElement }) => {
  const [findMetricId, setFindMetricId] = useState('');
  const [replaceMetricId, setReplaceMetricId] = useState('');
  const [initialAffectedContent, setInitialAffectedContent] = useState<
    FindAndReplace.MetricReceipt | undefined
  >(undefined);
  const [selectedAffectedContent, setSelectedAffectedContent] = useState<
    FindAndReplace.MetricReceipt | undefined
  >(undefined);
  const [isArchiveUnusedMetricSelected, setIsArchiveUnusedMetricSelected] =
    useState(false);

  const updateById = useCallback(
    (
      id: string,
      action: 'add' | 'remove',
      type: FindAndReplace.AffectedContentType,
    ) => {
      const key = getKeyType(type);

      if (!key || !initialAffectedContent) {
        return;
      }

      setSelectedAffectedContent((current) => {
        if (!current) {
          return;
        }

        const newAffectedContent = structuredClone(current);
        Object.keys(initialAffectedContent?.affectedContentIds).forEach((k) => {
          if (key === k) {
            if (action === 'add') {
              newAffectedContent.affectedContentIds[k].push(id);
            }
            if (action === 'remove') {
              newAffectedContent.affectedContentIds[k] =
                newAffectedContent.affectedContentIds[k].filter(
                  (item) => item !== id,
                );
            }
          }
        });
        return newAffectedContent;
      });
    },
    [initialAffectedContent],
  );

  return (
    <FindAndReplaceContext.Provider
      value={{
        findMetricId,
        replaceMetricId,
        setFindMetricId,
        setReplaceMetricId,
        setInitialAffectedContent,
        initialAffectedContent,
        selectedAffectedContent,
        setSelectedAffectedContent,
        updateById,
        isFindAndReplaceFlowActive: true,
        isArchiveUnusedMetricSelected,
        setIsArchiveUnusedMetricSelected,
      }}
    >
      {children}
    </FindAndReplaceContext.Provider>
  );
};

export { FindAndReplaceContext, FindAndReplaceProvider };
